import 'intro.js/introjs.css';
import './DocumentList.scss';
import '../DocumentStatuses.scss';

import React, { ChangeEvent, FC, SyntheticEvent, useContext, useEffect } from 'react';
import { AppContext, NotificationType } from 'components/AppContext/AppContext';
import NavHeading, { NavHeadingProps } from 'components/NavHeading/NavHeading';
import { NavHeadings } from 'constants/Constants';
import { useDataSet } from 'customHooks/useDataSet';
import { useGuidedNavigationCollection } from 'customHooks/useGuidedNavigationCollection';
import { useModifyEntityService } from 'customHooks/useModifyEntityService';
import { useTokenExpiredHandler } from 'customHooks/useTokenExpiredHandler';
import DataSetOperators, { SearchType, SortDirection } from 'interfaces/DataSetOperators';
import DataWrapper from 'interfaces/DataWrapper';
import { Document } from 'models/Document';
import { Entities } from 'models/Entities';
import { LogLevel } from 'models/LogLevel';
import moment from 'moment';
import { Col, Row } from 'react-bootstrap';
import DataTable from 'react-data-table-component';

import TableActionButtonGroup from '../../../components/TableActionButtonGroup/TableActionButtonGroup';
import TableSearch from '../../../components/TableSearch/TableSearch';
import { ExpandedDocumentView } from '../../../models/ExpandedDocumentView';
import AddDocumentButton from '../AddDocumentButton/AddDocumentButton';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface DocumentListProps { }

// eslint-disable-next-line no-empty-pattern
const DocumentList: FC<DocumentListProps> = ({ }) => {
  const { configRepo, selectedProjectId, setIsProjectSelectable, notify, sendLog, projects } = useContext(AppContext);
  const initialParams: DataSetOperators = {
    SortBy: 'plannedDate',
    SortDirection: SortDirection.Desc,
    Top: 10
  };
  const { dataSet, setOperators, operators } = useDataSet<ExpandedDocumentView>(
    configRepo!.SCHEDULE_API_BASE_URI,
    Entities.ExpandedDocumentView,
    selectedProjectId,
    initialParams,
  );
  const dataWrapper: DataWrapper<ExpandedDocumentView> =
    dataSet.responseData as DataWrapper<ExpandedDocumentView>;
  const documents: ExpandedDocumentView[] = dataWrapper?.data;
  const [resetPaginationToggle, setResetPaginationToggle] = React.useState(false);
  const [filterText, setFilterText] = React.useState('');
  const [headingTitle, setHeadingTitle] = React.useState<string>('Articles');

  useEffect(() => {
    setIsProjectSelectable(true);
  }, []);

  useEffect(() => {
    if (operators.ProjectId !== selectedProjectId) {
      setResetPaginationToggle(true);
      const ops = { ...operators };
      ops.Skip = 0;
      ops.ProjectId = selectedProjectId;
      ops.SortBy = 'plannedDate';
      ops.SortDirection = SortDirection.Desc;
      setOperators(ops);
    }
  }, [selectedProjectId]);

  useEffect(() => {
    if (projects && selectedProjectId) {
      const selectedProject = projects.find(p => p.id === selectedProjectId);
      setHeadingTitle(`${selectedProject ? selectedProject.name : ''} Articles`);
    }
  }, [selectedProjectId, projects]);

  const onTokenExpired = useTokenExpiredHandler();
  const { deleteEntity } = useModifyEntityService<Document>(
    configRepo!.SCHEDULE_API_BASE_URI,
    Entities.Documents,
    sendLog,
    onTokenExpired,
  );

  const onDelete = async (id: number): Promise<void> => {
    if (await deleteEntity(id)) {
      notify('Document has been deleted successfully.', NotificationType.Success);
      sendLog(`deleted successfully document: ${id}`, LogLevel.info);
      const ops = { ...operators };
      setOperators(ops);
    } else {
      notify('Document has not been deleted successfully.', NotificationType.Error);
      sendLog(`failed to delete document: ${id}`, LogLevel.error);
    }
  };

  const columns = [
    {
      name: 'Actions',
      // eslint-disable-next-line react/no-unstable-nested-components
      cell: (row: any) => (
        <TableActionButtonGroup
          entityContainerUrl={Entities.Documents}
          entityId={row.id}
          key={row.id}
          onDelete={onDelete}
          isViewEnabled
        />
      ),
      ignoreRowClick: true,
      allowOverflow: false,
      button: true,
      className: 'padd-left',
      width: '150px',
    },
    {
      name: 'Date',
      selector: (row: any) =>
        row.plannedDate ? moment(new Date(row.plannedDate)).format('MM/DD/YYYY') : 'UNPLANNED',
      type: 'PlannedDate',
      sortable: true,
      width: '120px',
    },
    {
      name: 'Status',
      // eslint-disable-next-line react/no-unstable-nested-components
      selector: (row: any) => (
        <span className={`${row.statusType} status-type`}>{row.statusName}</span>
      ),
      type: 'StatusName',
      sortable: true,
      width: '150px',
    },
    {
      name: 'Theme',
      selector: (row: any) => row.themeName,
      type: 'ThemeName',
      sortable: true,
      width: '120px',
    },
    {
      name: 'Thumbnail',
      // eslint-disable-next-line react/no-unstable-nested-components
      selector: (row: any) => (
        row.imageUrl ?
          <img src={row.imageUrl} alt={row.id} key={row.id} className="img-fluid p-1" /> :
          <img src="https://placehold.co/300x200?text=No+Image" alt={row.id} key={row.id} className="img-fluid p-1" />
      ),
      type: 'ImageUrl',
      sortable: false,
      width: '120px',
    },
    {
      name: 'Title',
      selector: (row: any) => row.title,
      type: 'Title',
      sortable: true,
      width: '400px',
    },
    {
      name: 'Primary Keyword',
      selector: (row: any) => row.keyword,
      type: 'Keyword',
      sortable: true,
      width: '200px',
    },
    {
      name: 'Volume',
      selector: (row: any) => row.searchVolume,
      type: 'SearchVolume',
      sortable: true,
      width: '100px',
    },
    {
      name: 'Difficulty',
      selector: (row: any) => row.difficulty,
      type: 'Difficulty',
      sortable: true,
      width: '100px',
    },
  ];

  const handleSort = (column: any, sortDirection: any) => {
    const ops = { ...operators };
    ops.SortBy = column.type;
    ops.SortDirection = sortDirection === 'asc' ? SortDirection.Asc : SortDirection.Desc;
    setOperators(ops);
  };

  const handlePerRowsChange = (page: number) => {
    const ops = { ...operators };
    ops.Top = page;
    setOperators(ops);
  };

  const handlePageChange = (page: number) => {
    const ops = { ...operators };
    if (page === 1) {
      ops.Skip = 0;
    } else {
      ops.Skip = ops.Top! * (page - 1);
    }
    setOperators(ops);
  };

  // see: https://react-data-table-component.netlify.app/?path=/docs/examples-filtering--filtering
  const onSearchTextChanged = (e: ChangeEvent<HTMLInputElement>) => {
    setFilterText(e.target.value);
  };

  const applySearch = (terms: string | undefined) => {
    const ops = { ...operators };
    if (terms) {
      ops.SearchProperty = 'Title|Keyword|StatusName|ThemeName';
      ops.SearchType = SearchType.Contains;
      ops.SearchValue = terms;
    } else {
      ops.SearchProperty = undefined;
      ops.SearchType = undefined;
      ops.SearchValue = undefined;
    }
    // this is necessary
    // test: switch project A > search 'T' > switch project B > clear search
    // => results are project A unfiltered without this
    ops.ProjectId = selectedProjectId;
    setOperators(ops);
  };

  const subHeaderComponentMemo = React.useMemo(() => {
    const handleClear = () => {
      if (filterText) {
        setResetPaginationToggle(!resetPaginationToggle);
        setFilterText('');
        applySearch(undefined);
      }
    };

    const onFilter = (e: SyntheticEvent) => {
      e.preventDefault();
      applySearch(filterText);
    };

    return (
      <div className="action-panel">
        <Row>
          <Col lg={{ span: 4, offset: 7 }} md={{ span: 6, offset: 4 }} sm={9} xs={9} className="g-0" style={{ 'textAlign': 'right' }}>
            <TableSearch
              onFilter={onFilter}
              onClear={handleClear}
              filterPropertyName="Title, Keyword, Status or Theme"
              searchText={filterText}
              onSearchTextChanged={onSearchTextChanged}
            />
          </Col>
          <Col lg={1} md={1} sm={3} xs={3} className="">
            <AddDocumentButton />
          </Col>
        </Row>
      </div>
    );
  }, [filterText, resetPaginationToggle, selectedProjectId]);

  const tour = useGuidedNavigationCollection();
  useEffect(() => {
    tour.startTourFromQueryParams();
  }, []);

  const heading: NavHeadingProps = {
    title: headingTitle,
    ...NavHeadings.Articles,
  };

  return (
    <div className=''>
      <NavHeading {...heading} />
      <div className="data-collection">
        <DataTable
          columns={columns}
          defaultSortFieldId="2"
          defaultSortAsc={false}
          className="tbl-documents"
          data={documents}
          sortServer
          onSort={handleSort}
          pagination
          paginationServer
          paginationTotalRows={dataWrapper?.metaData?.totalRecordCount ?? 0}
          paginationResetDefaultPage={resetPaginationToggle}
          onChangeRowsPerPage={handlePerRowsChange}
          onChangePage={handlePageChange}
          subHeader
          subHeaderComponent={subHeaderComponentMemo}
          persistTableHead
        // fixedHeader
        // fixedHeaderScrollHeight="500px"
        // dense
        />
      </div>
    </div>

  );
};

export default DocumentList;
